"use client";

import { useLocale, useTranslations } from "next-intl";
import { useMemo } from "react";
import { AvatarStack } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { XIcon, type XIconHandle } from "@/components/widget/icons/animated";
import { matchesBaseLocale } from "@/lib/localize-config-text";
import { useSessionContentContext } from "@/lib/targeting/session-content-context";
import { useMatchContext } from "@/lib/targeting/use-match-context";
import { useIconHover } from "@/lib/use-icon-hover";
import { DEFAULT_HOME_LAYOUT, type WidgetConfig, type WidgetTab } from "@/lib/widget-config";
import { CrossFade } from "../cross-fade";
import { filterHomeBlocks, HomeBlocks } from "../home-blocks";
import { pickHomeBlocks } from "../home-blocks/pick-home-blocks";
import { WexioMark } from "../icons/wexio-mark";
import { type PickedGreeting, useGreetingVariant } from "./use-greeting-variant";

interface HomeTabProps {
  config: WidgetConfig;
  /** Operator logo, already resolved to a single URL by the shell
   *  (`config.organizationLogo` is per-mode; the shell collapses it
   *  against the active theme mode). `null` → framed Wexio mark. */
  organizationLogoUrl: string | null;
  onClose: () => void;
  /** Open a top-level tab — Home blocks route into Messenger / Help /
   *  News rather than hydrating their content inline. */
  onOpenTab: (tab: WidgetTab) => void;
  /** Demo data mode — threaded to content-rendering blocks (news, etc.)
   *  so they pick between bundled samples and real BE data. */
  isDummy: boolean;
  /** Authenticated visitor chat id. `null` when the visitor hasn't
   *  auth'd yet — chat-bound blocks (`recent-message`) hide in that
   *  state. */
  chatId: string | null;
  /** Drill into a specific news article — `featured-article` calls this
   *  when the operator-picked post resolves. */
  onOpenNewsArticle?: (id: string) => void;
  /** Drill into a specific help article by slug — `pinned-articles`
   *  calls this when the operator-picked row resolves. */
  onOpenHelpArticle?: (slug: string) => void;
  /** Open the visitor's conversation directly (a thread) — `recent-message`
   *  is bound to a conversation, so it opens the thread, not the list. */
  onOpenThread?: () => void;
}

/**
 * Home tab — an operator-configured ordered list of blocks
 * (`config.homeLayout`, falling back to `DEFAULT_HOME_LAYOUT`).
 *
 * The header (logo + operator avatars + close) is fixed chrome and
 * scrolls away with content; everything below it is block-driven and
 * rendered by `<HomeBlocks>`. Blocks that depend on a disabled feature
 * are dropped via `filterHomeBlocks`.
 */
export function HomeTab({
  config,
  organizationLogoUrl,
  onClose,
  onOpenTab,
  isDummy,
  chatId,
  onOpenNewsArticle,
  onOpenHelpArticle,
  onOpenThread,
}: HomeTabProps) {
  const tHeader = useTranslations("header");
  const closeHover = useIconHover<XIconHandle>();

  // Contract v1: Home blocks as slots of variants (the session projection replaces the public
  // one), re-picked on every SPA navigation. Without slots, the legacy layout exactly as before.
  const sessionContent = useSessionContentContext();
  const matchCtx = useMatchContext();
  const homeSlots = sessionContent.slots?.homeSlots ?? config.homeSlots ?? null;
  const picked = useMemo(
    () => (homeSlots ? pickHomeBlocks(homeSlots, matchCtx) : null),
    [homeSlots, matchCtx],
  );
  const blocks = filterHomeBlocks(
    picked ? picked.blocks : (config.homeLayout ?? DEFAULT_HOME_LAYOUT),
    config.features,
  );

  // Greeting can be omitted entirely (operator set both lines empty).
  // Mirror the Greeting component's render guard here so we can lift
  // the first block off the header chrome — without the greeting the
  // blocks otherwise sit flush under the close/avatar row.
  // Demo always shows the localised default greeting; real mode only
  // when the operator configured a line. Mirror the `Greeting` guard.
  const slotGreeting = useGreetingVariant(config);
  const hasGreeting =
    slotGreeting !== undefined
      ? !!slotGreeting?.headline.trim() || !!slotGreeting?.subheadline.trim()
      : isDummy || !!config.greeting?.headline?.trim() || !!config.greeting?.subheadline?.trim();

  return (
    <ScrollArea followDirection className="flex-1">
      <div className="flex flex-col pb-6">
        {/* Inline header — scrolls away with content. The Wexio mark is
            ONLY a demo-mode placeholder so the marketing/Storybook view
            never paints a blank left chrome; in live mode an empty logo
            slot stays empty (operator hasn't uploaded one yet). */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          {organizationLogoUrl ? (
            // biome-ignore lint/performance/noImgElement: uploaded image of arbitrary host/dimensions; next/image needs known sizes + an allowlist
            <img
              src={organizationLogoUrl}
              alt={config.messenger?.title ?? ""}
              className="h-9 w-auto max-w-[140px] object-contain"
            />
          ) : isDummy ? (
            <WexioMark size={28} />
          ) : (
            <span className="h-9" aria-hidden="true" />
          )}
          <div className="flex items-center gap-1">
            {config.operatorAvatars.length > 0 && (
              <AvatarStack
                avatars={config.operatorAvatars}
                ringClass="ring-wx-bg-elevated/60"
                className="mr-2"
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={tHeader("close")}
              onClick={onClose}
              {...closeHover.parentHandlers}
            >
              <XIcon ref={closeHover.iconRef} size={18} />
            </Button>
          </div>
        </div>

        {/* Greeting — driven by the operator's `greeting.{headline,
            subheadline}` config. When the operator has set NEITHER
            field, the section is omitted entirely (no i18n placeholder
            fallback) so an unconfigured home tab doesn't surface a
            generic "Hi there 👋 / How can we help?" as if it were the
            operator's copy. */}
        <Greeting config={config} isDummy={isDummy} slotGreeting={slotGreeting} />

        <div className={hasGreeting ? undefined : "pt-4"}>
          <CrossFade enabled={!!picked} id={picked?.key ?? ""}>
            <HomeBlocks
              blocks={blocks}
              config={config}
              onOpenTab={onOpenTab}
              onOpenNewsArticle={onOpenNewsArticle}
              onOpenHelpArticle={onOpenHelpArticle}
              isDummy={isDummy}
              chatId={chatId}
              onOpenThread={onOpenThread}
            />
          </CrossFade>
        </div>
      </div>
    </ScrollArea>
  );
}

function Greeting({
  config,
  isDummy,
  slotGreeting,
}: {
  config: WidgetConfig;
  isDummy: boolean;
  slotGreeting: PickedGreeting | null | undefined;
}) {
  const t = useTranslations("home");
  const locale = useLocale();
  // Variant mode: the BE-rendered variant verbatim; no match = no greeting.
  if (slotGreeting !== undefined) {
    const head = slotGreeting?.headline.trim() ?? "";
    const sub = slotGreeting?.subheadline.trim() ?? "";
    return head || sub ? <GreetingLines headline={head} subheadline={sub} /> : null;
  }
  const operatorHeadline = config.greeting?.headline?.trim() ?? "";
  const operatorSubheadline = config.greeting?.subheadline?.trim() ?? "";
  const operatorHasAny = !!(operatorHeadline || operatorSubheadline);
  // Operator hasn't configured either line AND we're not in dummy/demo
  // mode — render nothing. The home gradient backdrop already carries
  // enough visual weight to anchor the top of the panel without a
  // generic "Hi there 👋" forced upon every integration.
  if (!isDummy && !operatorHasAny) return null;
  // Operator text is treated as written in their `defaultLocale`. When
  // the visitor's UI doesn't share that base language, we swap to the
  // widget's bundled per-locale greeting so an English-only operator
  // copy never leaks into a French / Japanese / Arabic session. For
  // matching locale (or dummy mode), use operator's wording verbatim —
  // empty fields stay empty (operator chose to skip that line).
  const operatorLocaleMatches = matchesBaseLocale(locale, config.defaultLocale ?? "en");
  // Use the operator's wording whenever they've SET a line (even in demo /
  // dashboard preview — otherwise a configured greeting never shows there);
  // still gated on a matching base locale so English copy can't leak into a
  // non-English session, which falls back to the bundled per-locale default.
  const useOperatorText = operatorHasAny && operatorLocaleMatches;
  const headline = useOperatorText ? operatorHeadline : t("greetingHeadline");
  const subheadline = useOperatorText ? operatorSubheadline : t("greetingSubheadline");
  if (!headline && !subheadline) return null;
  return <GreetingLines headline={headline} subheadline={subheadline} />;
}

function GreetingLines({ headline, subheadline }: { headline: string; subheadline: string }) {
  return (
    <section className="px-5 pt-2 pb-4">
      {headline && <p className="text-3xl leading-tight font-bold text-wx-fg-muted">{headline}</p>}
      {subheadline && (
        <p className="mt-1 text-3xl leading-tight font-bold text-wx-fg">{subheadline}</p>
      )}
    </section>
  );
}
